import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Clock,
  FileText,
  Package,
  Truck,
  TestTube,
  CheckCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./order-details.css";
import Loading from "../loading/Loading";
import { GlobalContext } from "../../GlobalContext";

// Add at the top, after imports
const formatPrice = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "₦0.00";
  }
  let numAmount;
  if (typeof amount === 'string') {
    const cleanAmount = amount.replace(/[₦,]/g, '').replace(/\.00$/, '');
    numAmount = parseFloat(cleanAmount);
  } else {
    numAmount = parseFloat(amount);
  }
  if (isNaN(numAmount)) {
    return "₦0.00";
  }
  return `₦${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OrderDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { order_id } = useParams();
  const { userData } = useContext(GlobalContext);

  const [order, setOrder] = useState(null);
  const [orderStatusTimeline, setOrderStatusTimeline] = useState([]);
  const [currentStatus, setCurrentStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requisitionFilled, setRequisitionFilled] = useState(false);

  const API_ENDPOINT = `/wp-json/dbsb/v1/orders/${order_id}`;
  
  // Get user role from GlobalContext
  const userRole = userData?.role === "author" ? "provider" : "patient";

  // Mapping of backend statuses to display labels
  const statusMapping = {
    paid: "Paid",
    scheduled: "Scheduled",
    collected: "Sample Collected",
    in_transit: "In Transit",
    testing: "Test in Progress",
    result_ready: "Result Ready",
  };

  // Helper function to convert UTC timestamp to WAT (UTC+1)
  const convertUTCToWAT = (utcTimestamp) => {
    if (!utcTimestamp) return null;
    // Parse the timestamp as UTC
    const utcDate = new Date(utcTimestamp);
    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      console.error("Invalid UTC timestamp:", utcTimestamp);
      return null;
    }
    // Add 1 hour (3600000 ms) for WAT (UTC+1)
    const watDate = new Date(utcDate.getTime() + 3600000);
    // Debug log to verify input and output
    console.log(
      "UTC Timestamp:",
      utcTimestamp,
      "WAT Date:",
      watDate.toISOString()
    );
    return watDate;
  };

  // Custom date formatter to match "Thur, 21 Jul 2025, 11:49AM" in WAT
  const formatStatusDate = (utcTimestamp) => {
    const watDate = convertUTCToWAT(utcTimestamp);
    if (!watDate) return "N/A";
    const options = {
      weekday: "short", // e.g., "Wed"
      day: "numeric", // e.g., "4"
      month: "short", // e.g., "Jun"
      year: "numeric", // e.g., "2025"
      hour: "numeric", // e.g., "3"
      minute: "2-digit", // e.g., "59"
      hour12: true, // e.g., "PM"
    };
    return watDate.toLocaleString("en-US", options).replace(/,/, ""); // Remove comma between date and time
  };

  // Formatter for note timestamp to match "DD MMM YYYY, HH:MM AM/PM" in WAT
  const formatNoteDate = (utcTimestamp) => {
    const watDate = convertUTCToWAT(utcTimestamp);
    if (!watDate) return "N/A";
    const options = {
      day: "2-digit", // e.g., "04"
      month: "short", // e.g., "Jun"
      year: "numeric", // e.g., "2025"
      hour: "numeric", // e.g., "3"
      minute: "2-digit", // e.g., "59"
      hour12: true, // e.g., "PM"
    };
    return watDate.toLocaleString("en-US", options).replace(/,/, ""); // Remove comma between date and time
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!order_id) {
        console.error("No order ID provided.");
        setError("No order found.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      
      // Reset requisition filled status to ensure fresh data
      setRequisitionFilled(false);

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "You must be logged in to view order details. Please log in and try again."
            );
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view this order.");
          } else if (response.status === 404) {
            throw new Error("No order found.");
          }
          throw new Error("Failed to fetch order details. Please try again.");
        }

        const data = await response.json();

        const statusSequence = [
          "paid",
          "scheduled",
          "collected",
          "in_transit",
          "testing",
          "result_ready",
        ];

        // Build timeline from status_history
        const timelineData =
          data.status_history?.reduce((acc, entry) => {
            if (statusSequence.includes(entry.status)) {
              acc[entry.status] = {
                timestamp: entry.timestamp,
                changed_by: entry.changed_by,
              };
            }
            return acc;
          }, {}) || {};

        const timeline = statusSequence.map((backendStatus) => {
          const displayStatus = statusMapping[backendStatus] || backendStatus;
          const timelineEntry = timelineData[backendStatus] || {};
          const latestStatus = data.status_history?.slice(-1)[0]?.status || "";
          const isActive =
            timelineEntry.timestamp &&
            (statusSequence.indexOf(backendStatus) <=
              statusSequence.indexOf(latestStatus) ||
              (backendStatus === "paid" && latestStatus === "processing"));
          return {
            status: displayStatus,
            date: timelineEntry.timestamp
              ? formatStatusDate(timelineEntry.timestamp)
              : "N/A",
            active: isActive,
          };
        });

        const patientDetails = {
          firstName: data.patient?.name
            ? data.patient.name.split(" ")[0]
            : "N/A",
          lastName: data.patient?.name
            ? data.patient.name.split(" ").slice(1).join(" ")
            : "N/A",
          age: data.patient?.age || "N/A",
          email: data.patient?.email || "N/A",
          phone: data.patient?.phone || "N/A",
        };

        const providerDetails =
          data.order_type === "provider" && data.healthcare_provider?.name
            ? {
                firstName: data.healthcare_provider.name.split(" ")[0] || "N/A",
                lastName:
                  data.healthcare_provider.name.split(" ").slice(1).join(" ") ||
                  "N/A",
                email: data.healthcare_provider.email || "N/A",
                phone: data.healthcare_provider.phone || "N/A",
              }
            : null;

        // Use all notes
        const allNotes = Array.isArray(data.notes)
          ? data.notes.sort(
              (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
            )
          : [];

        // Check if requisition form has been filled
        // The API returns requisition.filled as a boolean
        // If requisition object doesn't exist or filled is not explicitly true, consider it not filled
        const isRequisitionFilled = data.requisition && data.requisition.filled === true;
        console.log("Requisition data from API:", data.requisition);
        console.log("Requisition type:", data.requisition?.type);
        console.log("Online requisition data exists:", !!data.requisition?.online_data);
        console.log("Uploaded form exists:", !!data.requisition?.uploaded_form);
        console.log("Is requisition filled?", isRequisitionFilled);
        setRequisitionFilled(isRequisitionFilled);

        setOrder({
          orderId: data.order_id || "N/A",
          orderStatus: data.order_status || "N/A",
          orderDate: data.order_date
            ? new Date(data.order_date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
            : "N/A",
          orderType: data.order_type || "N/A",
          patient: patientDetails,
          paymentStatus: data.payment_status || "N/A",
          healthcareProvider: providerDetails,
          test: {
            name: data.test?.name || "N/A",
            price: data.test?.price || "N/A",
            estimatedProcessingTime:
              data.test?.estimated_processing_time || "N/A",
            sampleType: data.test?.sample_type || "N/A",
          },
          collectionMethod: data.collection_method || "N/A",
          deliveryMethod: "Email Only",
          notes: allNotes,
          requisitionForm: data.requisition?.uploaded_form || "N/A",
          payment_link: data.payment_link || "",
        });

        setOrderStatusTimeline(timeline);
        setCurrentStatus(data.order_status || "N/A");
        setLoading(false);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("No order found.");
        setOrder(null); // Ensure order is null to trigger error message
        setOrderStatusTimeline([]);
        setCurrentStatus("");
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [order_id, location.state?.timestamp]); // Refetch when timestamp changes (from requisition form submission)

  // Debug: Log requisition status changes
  useEffect(() => {
    console.log("Requisition filled status changed:", requisitionFilled);
  }, [requisitionFilled]);

  const statusIcons = {
    Paid: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.8907 10.5963C25.1441 13.8496 25.1441 19.1296 21.8907 22.3829C18.6374 25.6363 13.3574 25.6363 10.1041 22.3829C6.85073 19.1296 6.85073 13.8496 10.1041 10.5963C13.3574 7.34292 18.6374 7.34292 21.8907 10.5963Z"
          fill="#B3B7B9"
        />
        <path
          d="M11.0013 29.8545C10.8813 29.8545 10.748 29.8278 10.628 29.7878C7.62801 28.5878 5.20134 26.4678 3.57468 23.6678C2.00134 20.9345 1.37468 17.8411 1.78801 14.6945C1.85468 14.1478 2.37468 13.7611 2.90801 13.8278C3.45468 13.8945 3.84134 14.4011 3.77468 14.9478C3.42801 17.6545 3.96134 20.3211 5.30801 22.6678C6.69468 25.0678 8.78801 26.8945 11.3613 27.9211C11.868 28.1345 12.1213 28.7078 11.9213 29.2278C11.7747 29.6145 11.388 29.8545 11.0013 29.8545Z"
          fill="#B3B7B9"
        />
        <path
          d="M7.8073 6.9751C7.51397 6.9751 7.22063 6.84177 7.02063 6.58844C6.67397 6.16177 6.75397 5.5351 7.19397 5.18844C9.7273 3.20177 12.7806 2.14844 16.0073 2.14844C19.154 2.14844 22.154 3.16177 24.674 5.08177C25.114 5.4151 25.194 6.04177 24.8606 6.48177C24.5273 6.92177 23.9006 7.00177 23.4606 6.66844C21.2873 5.0151 18.714 4.14844 16.0073 4.14844C13.234 4.14844 10.6073 5.0551 8.42063 6.76177C8.23397 6.90844 8.02063 6.9751 7.8073 6.9751Z"
          fill="#B3B7B9"
        />
        <path
          d="M21.0035 29.8567C20.6035 29.8567 20.2302 29.6167 20.0702 29.2301C19.8702 28.7234 20.1102 28.1367 20.6302 27.9234C23.2035 26.8834 25.2969 25.0701 26.6835 22.6701C28.0435 20.3234 28.5769 17.6567 28.2169 14.9634C28.1502 14.4167 28.5369 13.9101 29.0835 13.8434C29.6169 13.7767 30.1369 14.1634 30.2035 14.7101C30.6035 17.8434 29.9902 20.9501 28.4169 23.6834C26.8035 26.4834 24.3635 28.5901 21.3635 29.8034C21.2569 29.8301 21.1369 29.8567 21.0035 29.8567Z"
          fill="#B3B7B9"
        />
      </svg>
    ),
    Scheduled: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.5841 2.66406H10.4107C5.5574 2.66406 2.66406 5.5574 2.66406 10.4107V21.5707C2.66406 26.4374 5.5574 29.3307 10.4107 29.3307H21.5707C26.4241 29.3307 29.3174 26.4374 29.3174 21.5841V10.4107C29.3307 5.5574 26.4374 2.66406 21.5841 2.66406ZM22.6641 22.9974H9.33073C8.78406 22.9974 8.33073 22.5441 8.33073 21.9974C8.33073 21.4507 8.78406 20.9974 9.33073 20.9974H22.6641C23.2107 20.9974 23.6641 21.4507 23.6641 21.9974C23.6641 22.5441 23.2107 22.9974 22.6641 22.9974ZM22.6641 16.9974H9.33073C8.78406 16.9974 8.33073 16.5441 8.33073 15.9974C8.33073 15.4507 8.78406 14.9974 9.33073 14.9974H22.6641C23.2107 14.9974 23.6641 15.4507 23.6641 15.9974C23.6641 16.5441 23.2107 16.9974 22.6641 16.9974ZM22.6641 10.9974H9.33073C8.78406 10.9974 8.33073 10.5441 8.33073 9.9974C8.33073 9.45073 8.78406 8.9974 9.33073 8.9974H22.6641C23.2107 8.9974 23.6641 9.45073 23.6641 9.9974C23.6641 10.5441 23.2107 10.9974 22.6641 10.9974Z"
          fill="#B3B7B9"
        />
      </svg>
    ),
    "Sample Collected": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M23.4621 7.08427L15.9287 3.03094C15.1287 2.60427 14.1821 2.60427 13.3821 3.03094L5.86208 7.08427C5.31542 7.39094 4.96875 7.9776 4.96875 8.6176C4.96875 9.27094 5.30208 9.8576 5.86208 10.1509L13.3954 14.2043C13.7954 14.4176 14.2354 14.5243 14.6621 14.5243C15.0888 14.5243 15.5421 14.4176 15.9287 14.2043L23.4621 10.1509C24.0088 9.8576 24.3554 9.27094 24.3554 8.6176C24.3554 7.9776 24.0088 7.39094 23.4621 7.08427Z"
          fill="#B3B7B9"
        />
        <path
          d="M12.1574 15.6193L5.1574 12.126C4.61073 11.846 3.9974 11.886 3.4774 12.1926C2.97073 12.5126 2.66406 13.0593 2.66406 13.6593V20.2726C2.66406 21.4193 3.30406 22.446 4.33073 22.966L11.3307 26.4593C11.5707 26.5793 11.8374 26.646 12.1041 26.646C12.4107 26.646 12.7307 26.5526 13.0107 26.3926C13.5174 26.0726 13.8241 25.526 13.8241 24.926V18.3126C13.8107 17.166 13.1707 16.1393 12.1574 15.6193Z"
          fill="#B3B7B9"
        />
        <path
          d="M26.6701 13.6593V16.9393C26.0301 16.7526 25.3501 16.6726 24.6701 16.6726C22.8568 16.6726 21.0834 17.2993 19.6834 18.4193C17.7634 19.926 16.6701 22.206 16.6701 24.6726C16.6701 25.326 16.7501 25.9793 16.9234 26.606C16.7234 26.5793 16.5234 26.4993 16.3368 26.3793C15.8301 26.0726 15.5234 25.526 15.5234 24.926V18.3126C15.5234 17.166 16.1634 16.1393 17.1768 15.6193L24.1768 12.126C24.7234 11.846 25.3368 11.886 25.8568 12.1926C26.3634 12.5126 26.6701 13.0593 26.6701 13.6593Z"
          fill="#B3B7B9"
        />
        <path
          d="M29.3041 20.8953C28.2107 19.5486 26.5441 18.6953 24.6641 18.6953C23.2507 18.6953 21.9441 19.1886 20.9174 20.0153C19.5307 21.1086 18.6641 22.802 18.6641 24.6953C18.6641 25.8153 18.9841 26.882 19.5307 27.7886C19.8907 28.3886 20.3441 28.9086 20.8774 29.3353H20.8907C21.9174 30.1886 23.2374 30.6953 24.6641 30.6953C26.1841 30.6953 27.5574 30.1353 28.6107 29.202C29.0774 28.802 29.4774 28.322 29.7974 27.7886C30.3441 26.882 30.6641 25.8153 30.6641 24.6953C30.6641 23.2553 30.1574 21.922 29.3041 20.8953ZM27.6774 23.9486L24.4774 26.9086C24.2907 27.082 24.0374 27.1753 23.7974 27.1753C23.5441 27.1753 23.2907 27.082 23.0907 26.882L21.6107 25.402C21.2241 25.0153 21.2241 24.3753 21.6107 23.9886C21.9974 23.602 22.6374 23.602 23.0241 23.9886L23.8241 24.7886L26.3174 22.482C26.7174 22.1086 27.3574 22.1353 27.7307 22.5353C28.1174 22.9486 28.0907 23.5753 27.6774 23.9486Z"
          fill="#B3B7B9"
        />
      </svg>
    ),
    "In Transit": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M28.6641 20.6641C29.0374 20.6641 29.3307 20.9574 29.3307 21.3307V22.6641C29.3307 24.8774 27.5441 26.6641 25.3307 26.6641C25.3307 24.4641 23.5307 22.6641 21.3307 22.6641C19.1307 22.6641 17.3307 24.4641 17.3307 26.6641H14.6641C14.6641 24.4641 12.8641 22.6641 10.6641 22.6641C8.46406 22.6641 6.66406 24.4641 6.66406 26.6641C4.45073 26.6641 2.66406 24.8774 2.66406 22.6641V19.9974C2.66406 19.2641 3.26406 18.6641 3.9974 18.6641H16.6641C18.5041 18.6641 19.9974 17.1707 19.9974 15.3307V7.9974C19.9974 7.26406 20.5974 6.66406 21.3307 6.66406H22.4507C23.4107 6.66406 24.2907 7.18406 24.7707 8.01073L25.6241 9.50406C25.7441 9.7174 25.5841 9.9974 25.3307 9.9974C23.4907 9.9974 21.9974 11.4907 21.9974 13.3307V17.3307C21.9974 19.1707 23.4907 20.6641 25.3307 20.6641H28.6641Z"
          fill="#B3B7B9"
        />
        <path
          d="M10.6667 29.3333C12.1394 29.3333 13.3333 28.1394 13.3333 26.6667C13.3333 25.1939 12.1394 24 10.6667 24C9.19391 24 8 25.1939 8 26.6667C8 28.1394 9.19391 29.3333 10.6667 29.3333Z"
          fill="#B3B7B9"
        />
        <path
          d="M21.3307 29.3333C22.8035 29.3333 23.9974 28.1394 23.9974 26.6667C23.9974 25.1939 22.8035 24 21.3307 24C19.858 24 18.6641 25.1939 18.6641 26.6667C18.6641 28.1394 19.858 29.3333 21.3307 29.3333Z"
          fill="#B3B7B9"
        />
        <path
          d="M29.3333 16.7067V18.6667H25.3333C24.6 18.6667 24 18.0667 24 17.3333V13.3333C24 12.6 24.6 12 25.3333 12H27.0533L28.9867 15.3867C29.2133 15.7867 29.3333 16.24 29.3333 16.7067Z"
          fill="#B3B7B9"
        />
        <path
          d="M17.4374 2.66406H7.58406C4.86406 2.66406 2.66406 4.86406 2.66406 7.58406V16.0907C2.66406 16.8241 3.26406 17.4241 3.9974 17.4241H16.1974C17.5574 17.4241 18.6641 16.3174 18.6641 14.9574V3.89073C18.6641 3.21073 18.1174 2.66406 17.4374 2.66406ZM13.4241 9.42406L10.6374 12.1174C10.4374 12.3041 10.1841 12.3974 9.94406 12.3974C9.69073 12.3974 9.4374 12.3041 9.25073 12.1174L7.90406 10.8374C7.50406 10.4507 7.49073 9.81073 7.8774 9.41073C8.25073 9.01073 8.89073 9.01073 9.29073 9.38406L9.94406 10.0107L12.0374 7.98406C12.4374 7.5974 13.0641 7.61073 13.4507 8.01073C13.8374 8.41073 13.8241 9.0374 13.4241 9.42406Z"
          fill="#B3B7B9"
        />
      </svg>
    ),
    "Test in Progress": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M27.1403 16.9754C26.6469 16.9754 26.2336 16.602 26.1803 16.1087C25.8603 13.1754 24.2869 10.5354 21.8603 8.85538C21.4203 8.54872 21.3136 7.94872 21.6203 7.50872C21.9269 7.06872 22.5269 6.96205 22.9669 7.26872C25.8603 9.28205 27.7269 12.4287 28.1136 15.9087C28.1669 16.4421 27.7803 16.922 27.2469 16.9754C27.2069 16.9754 27.1803 16.9754 27.1403 16.9754Z"
          fill="#B3B7B9"
        />
        <path
          d="M4.98835 17.0442C4.96169 17.0442 4.92169 17.0442 4.89502 17.0442C4.36169 16.9908 3.97502 16.5108 4.02835 15.9775C4.38835 12.4975 6.22835 9.35084 9.09502 7.32417C9.52169 7.01751 10.135 7.12417 10.4417 7.55084C10.7484 7.99084 10.6417 8.59084 10.215 8.89751C7.81502 10.6042 6.25502 13.2442 5.96169 16.1642C5.90835 16.6708 5.48169 17.0442 4.98835 17.0442Z"
          fill="#B3B7B9"
        />
        <path
          d="M21.3182 28.1312C19.6782 28.9178 17.9182 29.3178 16.0782 29.3178C14.1582 29.3178 12.3315 28.8912 10.6248 28.0245C10.1448 27.7978 9.95818 27.2112 10.1982 26.7312C10.4248 26.2512 11.0115 26.0645 11.4915 26.2912C12.3315 26.7178 13.2248 27.0112 14.1315 27.1845C15.3582 27.4245 16.6115 27.4378 17.8382 27.2245C18.7448 27.0645 19.6382 26.7845 20.4648 26.3845C20.9582 26.1578 21.5448 26.3445 21.7582 26.8378C21.9982 27.3178 21.8115 27.9045 21.3182 28.1312Z"
          fill="#B3B7B9"
        />
        <path
          d="M16.0647 2.67969C13.998 2.67969 12.3047 4.35969 12.3047 6.43969C12.3047 8.51969 13.9847 10.1997 16.0647 10.1997C18.1447 10.1997 19.8247 8.51969 19.8247 6.43969C19.8247 4.35969 18.1447 2.67969 16.0647 2.67969Z"
          fill="#B3B7B9"
        />
        <path
          d="M6.72875 18.4922C4.66208 18.4922 2.96875 20.1722 2.96875 22.2522C2.96875 24.3322 4.64875 26.0122 6.72875 26.0122C8.80875 26.0122 10.4888 24.3322 10.4888 22.2522C10.4888 20.1722 8.79542 18.4922 6.72875 18.4922Z"
          fill="#B3B7B9"
        />
        <path
          d="M25.2678 18.4922C23.2011 18.4922 21.5078 20.1722 21.5078 22.2522C21.5078 24.3322 23.1878 26.0122 25.2678 26.0122C27.3478 26.0122 29.0278 24.3322 29.0278 22.2522C29.0278 20.1722 27.3478 18.4922 25.2678 18.4922Z"
          fill="#B3B7B9"
        />
      </svg>
    ),
    "Result Ready": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.9974 2.66406C8.65073 2.66406 2.66406 8.65073 2.66406 15.9974C2.66406 23.3441 8.65073 29.3307 15.9974 29.3307C23.3441 29.3307 29.3307 23.3441 29.3307 15.9974C29.3307 8.65073 23.3441 2.66406 15.9974 2.66406ZM22.3707 12.9307L14.8107 20.4907C14.6241 20.6774 14.3707 20.7841 14.1041 20.7841C13.8374 20.7841 13.5841 20.6774 13.3974 20.4907L9.62406 16.7174C9.2374 16.3307 9.2374 15.6907 9.62406 15.3041C10.0107 14.9174 10.6507 14.9174 11.0374 15.3041L14.1041 18.3707L20.9574 11.5174C21.3441 11.1307 21.9841 11.1307 22.3707 11.5174C22.7574 11.9041 22.7574 12.5307 22.3707 12.9307Z"
          fill="#B3B7B9"
        />
      </svg>
    ),
  };

  const activeStatusIcons = {
    Paid: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.8907 10.5963C25.1441 13.8496 25.1441 19.1296 21.8907 22.3829C18.6374 25.6363 13.3574 25.6363 10.1041 22.3829C6.85073 19.1296 6.85073 13.8496 10.1041 10.5963C13.3574 7.34292 18.6374 7.34292 21.8907 10.5963Z"
          fill="#3A3F6A"
        />
        <path
          d="M11.0013 29.8545C10.8813 29.8545 10.748 29.8278 10.628 29.7878C7.62801 28.5878 5.20134 26.4678 3.57468 23.6678C2.00134 20.9345 1.37468 17.8411 1.78801 14.6945C1.85468 14.1478 2.37468 13.7611 2.90801 13.8278C3.45468 13.8945 3.84134 14.4011 3.77468 14.9478C3.42801 17.6545 3.96134 20.3211 5.30801 22.6678C6.69468 25.0678 8.78801 26.8945 11.3613 27.9211C11.868 28.1345 12.1213 28.7078 11.9213 29.2278C11.7747 29.6145 11.388 29.8545 11.0013 29.8545Z"
          fill="#3A3F6A"
        />
        <path
          d="M7.8073 6.9751C7.51397 6.9751 7.22063 6.84177 7.02063 6.58844C6.67397 6.16177 6.75397 5.5351 7.19397 5.18844C9.7273 3.20177 12.7806 2.14844 16.0073 2.14844C19.154 2.14844 22.154 3.16177 24.674 5.08177C25.114 5.4151 25.194 6.04177 24.8606 6.48177C24.5273 6.92177 23.9006 7.00177 23.4606 6.66844C21.2873 5.0151 18.714 4.14844 16.0073 4.14844C13.234 4.14844 10.6073 5.0551 8.42063 6.76177C8.23397 6.90844 8.02063 6.9751 7.8073 6.9751Z"
          fill="#3A3F6A"
        />
        <path
          d="M21.0035 29.8567C20.6035 29.8567 20.2302 29.6167 20.0702 29.2301C19.8702 28.7234 20.1102 28.1367 20.6302 27.9234C23.2035 26.8834 25.2969 25.0701 26.6835 22.6701C28.0435 20.3234 28.5769 17.6567 28.2169 14.9634C28.1502 14.4167 28.5369 13.9101 29.0835 13.8434C29.6169 13.7767 30.1369 14.1634 30.2035 14.7101C30.6035 17.8434 29.9902 20.9501 28.4169 23.6834C26.8035 26.4834 24.3635 28.5901 21.3635 29.8034C21.2569 29.8301 21.1369 29.8567 21.0035 29.8567Z"
          fill="#3A3F6A"
        />
      </svg>
    ),
    Scheduled: (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.5841 2.66406H10.4107C5.5574 2.66406 2.66406 5.5574 2.66406 10.4107V21.5707C2.66406 26.4374 5.5574 29.3307 10.4107 29.3307H21.5707C26.4241 29.3307 29.3174 26.4374 29.3174 21.5841V10.4107C29.3307 5.5574 26.4374 2.66406 21.5841 2.66406ZM22.6641 22.9974H9.33073C8.78406 22.9974 8.33073 22.5441 8.33073 21.9974C8.33073 21.4507 8.78406 20.9974 9.33073 20.9974H22.6641C23.2107 20.9974 23.6641 21.4507 23.6641 21.9974C23.6641 22.5441 23.2107 22.9974 22.6641 22.9974ZM22.6641 16.9974H9.33073C8.78406 16.9974 8.33073 16.5441 8.33073 15.9974C8.33073 15.4507 8.78406 14.9974 9.33073 14.9974H22.6641C23.2107 14.9974 23.6641 15.4507 23.6641 15.9974C23.6641 16.5441 23.2107 16.9974 22.6641 16.9974ZM22.6641 10.9974H9.33073C8.78406 10.9974 8.33073 10.5441 8.33073 9.9974C8.33073 9.45073 8.78406 8.9974 9.33073 8.9974H22.6641C23.2107 8.9974 23.6641 9.45073 23.6641 9.9974C23.6641 10.5441 23.2107 10.9974 22.6641 10.9974Z"
          fill="#3A3F6A"
        />
      </svg>
    ),
    "Sample Collected": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M23.4621 7.08427L15.9287 3.03094C15.1287 2.60427 14.1821 2.60427 13.3821 3.03094L5.86208 7.08427C5.31542 7.39094 4.96875 7.9776 4.96875 8.6176C4.96875 9.27094 5.30208 9.8576 5.86208 10.1509L13.3954 14.2043C13.7954 14.4176 14.2354 14.5243 14.6621 14.5243C15.0888 14.5243 15.5421 14.4176 15.9287 14.2043L23.4621 10.1509C24.0088 9.8576 24.3554 9.27094 24.3554 8.6176C24.3554 7.9776 24.0088 7.39094 23.4621 7.08427Z"
          fill="#3A3F6A"
        />
        <path
          d="M12.1574 15.6193L5.1574 12.126C4.61073 11.846 3.9974 11.886 3.4774 12.1926C2.97073 12.5126 2.66406 13.0593 2.66406 13.6593V20.2726C2.66406 21.4193 3.30406 22.446 4.33073 22.966L11.3307 26.4593C11.5707 26.5793 11.8374 26.646 12.1041 26.646C12.4107 26.646 12.7307 26.5526 13.0107 26.3926C13.5174 26.0726 13.8241 25.526 13.8241 24.926V18.3126C13.8107 17.166 13.1707 16.1393 12.1574 15.6193Z"
          fill="#3A3F6A"
        />
        <path
          d="M26.6701 13.6593V16.9393C26.0301 16.7526 25.3501 16.6726 24.6701 16.6726C22.8568 16.6726 21.0834 17.2993 19.6834 18.4193C17.7634 19.926 16.6701 22.206 16.6701 24.6726C16.6701 25.326 16.7501 25.9793 16.9234 26.606C16.7234 26.5793 16.5234 26.4993 16.3368 26.3793C15.8301 26.0726 15.5234 25.526 15.5234 24.926V18.3126C15.5234 17.166 16.1634 16.1393 17.1768 15.6193L24.1768 12.126C24.7234 11.846 25.3368 11.886 25.8568 12.1926C26.3634 12.5126 26.6701 13.0593 26.6701 13.6593Z"
          fill="#3A3F6A"
        />
        <path
          d="M29.3041 20.8953C28.2107 19.5486 26.5441 18.6953 24.6641 18.6953C23.2507 18.6953 21.9441 19.1886 20.9174 20.0153C19.5307 21.1086 18.6641 22.802 18.6641 24.6953C18.6641 25.8153 18.9841 26.882 19.5307 27.7886C19.8907 28.3886 20.3441 28.9086 20.8774 29.3353H20.8907C21.9174 30.1886 23.2374 30.6953 24.6641 30.6953C26.1841 30.6953 27.5574 30.1353 28.6107 29.202C29.0774 28.802 29.4774 28.322 29.7974 27.7886C30.3441 26.882 30.6641 25.8153 30.6641 24.6953C30.6641 23.2553 30.1574 21.922 29.3041 20.8953ZM27.6774 23.9486L24.4774 26.9086C24.2907 27.082 24.0374 27.1753 23.7974 27.1753C23.5441 27.1753 23.2907 27.082 23.0907 26.882L21.6107 25.402C21.2241 25.0153 21.2241 24.3753 21.6107 23.9886C21.9974 23.602 22.6374 23.602 23.0241 23.9886L23.8241 24.7886L26.3174 22.482C26.7174 22.1086 27.3574 22.1353 27.7307 22.5353C28.1174 22.9486 28.0907 23.5753 27.6774 23.9486Z"
          fill="#3A3F6A"
        />
      </svg>
    ),
    "In Transit": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M28.6641 20.6641C29.0374 20.6641 29.3307 20.9574 29.3307 21.3307V22.6641C29.3307 24.8774 27.5441 26.6641 25.3307 26.6641C25.3307 24.4641 23.5307 22.6641 21.3307 22.6641C19.1307 22.6641 17.3307 24.4641 17.3307 26.6641H14.6641C14.6641 24.4641 12.8641 22.6641 10.6641 22.6641C8.46406 22.6641 6.66406 24.4641 6.66406 26.6641C4.45073 26.6641 2.66406 24.8774 2.66406 22.6641V19.9974C2.66406 19.2641 3.26406 18.6641 3.9974 18.6641H16.6641C18.5041 18.6641 19.9974 17.1707 19.9974 15.3307V7.9974C19.9974 7.26406 20.5974 6.66406 21.3307 6.66406H22.4507C23.4107 6.66406 24.2907 7.18406 24.7707 8.01073L25.6241 9.50406C25.7441 9.7174 25.5841 9.9974 25.3307 9.9974C23.4907 9.9974 21.9974 11.4907 21.9974 13.3307V17.3307C21.9974 19.1707 23.4907 20.6641 25.3307 20.6641H28.6641Z"
          fill="#3A3F6A"
        />
        <path
          d="M10.6667 29.3333C12.1394 29.3333 13.3333 28.1394 13.3333 26.6667C13.3333 25.1939 12.1394 24 10.6667 24C9.19391 24 8 25.1939 8 26.6667C8 28.1394 9.19391 29.3333 10.6667 29.3333Z"
          fill="#3A3F6A"
        />
        <path
          d="M21.3307 29.3333C22.8035 29.3333 23.9974 28.1394 23.9974 26.6667C23.9974 25.1939 22.8035 24 21.3307 24C19.858 24 18.6641 25.1939 18.6641 26.6667C18.6641 28.1394 19.858 29.3333 21.3307 29.3333Z"
          fill="#3A3F6A"
        />
        <path
          d="M29.3333 16.7067V18.6667H25.3333C24.6 18.6667 24 18.0667 24 17.3333V13.3333C24 12.6 24.6 12 25.3333 12H27.0533L28.9867 15.3867C29.2133 15.7867 29.3333 16.24 29.3333 16.7067Z"
          fill="#3A3F6A"
        />
        <path
          d="M17.4374 2.66406H7.58406C4.86406 2.66406 2.66406 4.86406 2.66406 7.58406V16.0907C2.66406 16.8241 3.26406 17.4241 3.9974 17.4241H16.1974C17.5574 17.4241 18.6641 16.3174 18.6641 14.9574V3.89073C18.6641 3.21073 18.1174 2.66406 17.4374 2.66406ZM13.4241 9.42406L10.6374 12.1174C10.4374 12.3041 10.1841 12.3974 9.94406 12.3974C9.69073 12.3974 9.4374 12.3041 9.25073 12.1174L7.90406 10.8374C7.50406 10.4507 7.49073 9.81073 7.8774 9.41073C8.25073 9.01073 8.89073 9.01073 9.29073 9.38406L9.94406 10.0107L12.0374 7.98406C12.4374 7.5974 13.0641 7.61073 13.4507 8.01073C13.8374 8.41073 13.8241 9.0374 13.4241 9.42406Z"
          fill="#3A3F6A"
        />
      </svg>
    ),
    "Test in Progress": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M27.1403 16.9754C26.6469 16.9754 26.2336 16.602 26.1803 16.1087C25.8603 13.1754 24.2869 10.5354 21.8603 8.85538C21.4203 8.54872 21.3136 7.94872 21.6203 7.50872C21.9269 7.06872 22.5269 6.96205 22.9669 7.26872C25.8603 9.28205 27.7269 12.4287 28.1136 15.9087C28.1669 16.4421 27.7803 16.922 27.2469 16.9754C27.2069 16.9754 27.1803 16.9754 27.1403 16.9754Z"
          fill="#3A3F6A"
        />
        <path
          d="M4.98835 17.0442C4.96169 17.0442 4.92169 17.0442 4.89502 17.0442C4.36169 16.9908 3.97502 16.5108 4.02835 15.9775C4.38835 12.4975 6.22835 9.35084 9.09502 7.32417C9.52169 7.01751 10.135 7.12417 10.4417 7.55084C10.7484 7.99084 10.6417 8.59084 10.215 8.89751C7.81502 10.6042 6.25502 13.2442 5.96169 16.1642C5.90835 16.6708 5.48169 17.0442 4.98835 17.0442Z"
          fill="#3A3F6A"
        />
        <path
          d="M21.3182 28.1312C19.6782 28.9178 17.9182 29.3178 16.0782 29.3178C14.1582 29.3178 12.3315 28.8912 10.6248 28.0245C10.1448 27.7978 9.95818 27.2112 10.1982 26.7312C10.4248 26.2512 11.0115 26.0645 11.4915 26.2912C12.3315 26.7178 13.2248 27.0112 14.1315 27.1845C15.3582 27.4245 16.6115 27.4378 17.8382 27.2245C18.7448 27.0645 19.6382 26.7845 20.4648 26.3845C20.9582 26.1578 21.5448 26.3445 21.7582 26.8378C21.9982 27.3178 21.8115 27.9045 21.3182 28.1312Z"
          fill="#3A3F6A"
        />
        <path
          d="M16.0647 2.67969C13.998 2.67969 12.3047 4.35969 12.3047 6.43969C12.3047 8.51969 13.9847 10.1997 16.0647 10.1997C18.1447 10.1997 19.8247 8.51969 19.8247 6.43969C19.8247 4.35969 18.1447 2.67969 16.0647 2.67969Z"
          fill="#3A3F6A"
        />
        <path
          d="M6.72875 18.4922C4.66208 18.4922 2.96875 20.1722 2.96875 22.2522C2.96875 24.3322 4.64875 26.0122 6.72875 26.0122C8.80875 26.0122 10.4888 24.3322 10.4888 22.2522C10.4888 20.1722 8.79542 18.4922 6.72875 18.4922Z"
          fill="#3A3F6A"
        />
        <path
          d="M25.2678 18.4922C23.2011 18.4922 21.5078 20.1722 21.5078 22.2522C21.5078 24.3322 23.1878 26.0122 25.2678 26.0122C27.3478 26.0122 29.0278 24.3322 29.0278 22.2522C29.0278 20.1722 27.3478 18.4922 25.2678 18.4922Z"
          fill="#3A3F6A"
        />
      </svg>
    ),
    "Result Ready": (
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.9974 2.66406C8.65073 2.66406 2.66406 8.65073 2.66406 15.9974C2.66406 23.3441 8.65073 29.3307 15.9974 29.3307C23.3441 29.3307 29.3307 23.3441 29.3307 15.9974C29.3307 8.65073 23.3441 2.66406 15.9974 2.66406ZM22.3707 12.9307L14.8107 20.4907C14.6241 20.6774 14.3707 20.7841 14.1041 20.7841C13.8374 20.7841 13.5841 20.6774 13.3974 20.4907L9.62406 16.7174C9.2374 16.3307 9.2374 15.6907 9.62406 15.3041C10.0107 14.9174 10.6507 14.9174 11.0374 15.3041L14.1041 18.3707L20.9574 11.5174C21.3441 11.1307 21.9841 11.1307 22.3707 11.5174C22.7574 11.9041 22.7574 12.5307 22.3707 12.9307Z"
          fill="#3A3F6A"
        />
      </svg>
    ),
  };

  const getProfilePic = (firstName) => {
    const firstLetter = firstName?.charAt(0).toUpperCase() || "?";
    return (
      <motion.div
        className="profile-pic"
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        <span>{firstLetter}</span>
      </motion.div>
    );
  };

  const isHalfwayLine = (index) => {
    const statusSequence = [
      "Paid",
      "Scheduled",
      "Sample Collected",
      "In Transit",
      "Test in Progress",
      "Result Ready",
    ];
    const backendToDisplay = {
      paid: "Paid",
      scheduled: "Scheduled",
      collected: "Sample Collected",
      in_transit: "In Transit",
      testing: "Test in Progress",
      result_ready: "Result Ready",
      processing: "Paid", // Map processing to Paid for timeline display
    };
    const activeDisplayStatus = backendToDisplay[currentStatus] || "";
    const currentIndex = statusSequence.indexOf(activeDisplayStatus);
    return index === currentIndex && index < statusSequence.length - 1;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
        duration: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.03, transition: { duration: 0.2 } },
    tap: { scale: 0.97 },
  };

  const statusIconVariants = {
    inactive: { scale: 1 },
    active: {
      scale: [1, 1.1, 1],
      transition: {
        repeat: 0,
        duration: 0.5,
      },
    },
  };

  if (loading) {
    return (
      <div className="order-details-container">
        <Loading />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-container">
        <p className="error">No order found</p>
      </div>
    );
  }

  return (
    <motion.div
      className="order-details-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <motion.div
        className="order-header"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="order-id-status" variants={itemVariants}>
          <h2>Order ID: {order.orderId}</h2>
          <motion.span
            className={`status ${currentStatus
              .toLowerCase()
              .replace(" ", "-")}`}
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            {currentStatus.toUpperCase()}
          </motion.span>
        </motion.div>
        {!requisitionFilled ? (
          <motion.button
            className="fill-requisition-button"
            onClick={() => {
              navigate(`/order-test?order_id=${order.orderId}`);
            }}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Fill requisition form
          </motion.button>
        ) : currentStatus === "pending" || currentStatus === "processing" ? (
          <motion.button
            className="make-payment-button"
            onClick={() => {
              if (order.payment_link) {
                window.location.href = order.payment_link;
              } else {
                console.error("Payment link not available");
              }
            }}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Make Payment
          </motion.button>
        ) : (
          <motion.button
            className="contact-support-button"
            onClick={() =>
              navigate(`/contact-support?order_id=${order.orderId}`, {
                state: {
                  fromPatientDetails:
                    location.state?.fromPatientDetails || false,
                  patientId: location.state?.patientId || null,
                },
              })
            }
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Contact support
          </motion.button>
        )}
      </motion.div>

      <AnimatePresence>
        {currentStatus === "result_ready" && (
          <motion.div
            className="notification-container"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/notification.png`}
              alt="Notification"
            />
            <p>
              Your test results for {order.test.name} are now available. The
              report has been securely sent to the registered email.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="status-timeline-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
      >
        <div className="status-timeline">
          {orderStatusTimeline.map((item, index) => (
            <motion.div
              key={index}
              className={`status-item ${item.active ? "active" : ""} ${
                isHalfwayLine(index) ? "halfway" : ""
              } ${index === orderStatusTimeline.length - 1 ? "last" : ""}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
            >
              <motion.div
                className={`status-icon ${item.active ? "active" : ""}`}
                variants={statusIconVariants}
                initial="inactive"
                animate={item.active ? "active" : "inactive"}
              >
                {item.active
                  ? activeStatusIcons[item.status]
                  : statusIcons[item.status]}
              </motion.div>
              <div className="status-label">{item.status}</div>
              <div className="status-date-placeholder">
                {item.active && item.date && item.date !== "N/A" && (
                  <motion.div
                    className="status-date"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                  >
                    {item.date}
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        className="details-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="test-order-details" variants={itemVariants}>
          <motion.div
            className="test-name-price"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <p>{order.test.name}</p>
            <p>
              {order.test.price !== "N/A"
                ? formatPrice(order.test.price)
                : "N/A"}
            </p>
          </motion.div>
          <ul></ul>
          

          
          <motion.div
            className="total"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <span>Total</span>
            <span>
              {order.test.price !== "N/A"
                ? formatPrice(order.test.price)
                : "N/A"}
            </span>
          </motion.div>
        </motion.div>
        <motion.div className="order-details" variants={itemVariants}>
          <h3>Order details</h3>
          {[
            { label: "Order date", value: order.orderDate },
            {
              label: "Patient name",
              value: `${order.patient.firstName} ${order.patient.lastName}`,
            },
            ...(order.patient.age !== "N/A"
              ? [{ label: "Patient age", value: order.patient.age }]
              : []),
            { label: "Patient email", value: order.patient.email },
            { label: "Patient phone", value: order.patient.phone },
            { label: "Payment method", value: "Paystack" },
            { label: "Payment status", value: order.paymentStatus },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="detail-item"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.3 }}
              whileHover={{ x: 3 }}
            >
              <span>{item.label}</span>
              <span>{item.value}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        className="details-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.2 }}
      >
        <motion.div className="test-details" variants={itemVariants}>
          <h3>Test details</h3>
          {[
            { label: "Test name", value: order.test.name },
            {
              label: "Price",
              value:
                order.test.price !== "N/A"
                  ? formatPrice(order.test.price)
                  : "N/A",
              className: "price-detail",
            },
            {
              label: "Estimated processing time",
              value: order.test.estimatedProcessingTime,
            },
            {
              label: "Sample type required",
              value: order.test.sampleType,
              className: "sample-type-item",
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              className={`detail-item ${item.className || ""}`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.3 }}
              whileHover={{ x: 3 }}
            >
              <span>{item.label}</span>
              <span>{item.value}</span>
            </motion.div>
          ))}
        </motion.div>
        <motion.div className="healthcare-report" variants={itemVariants}>
          {order.healthcareProvider && (
            <motion.div
              className="healthcare-provider"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <h3>Healthcare provider</h3>
              <motion.div
                className="provider-details"
                whileHover={{ scale: 1.01 }}
              >
                <div className="provider-info">
                  <span className="provider-name">{`${order.healthcareProvider.firstName} ${order.healthcareProvider.lastName}`}</span>
                  <span>{order.healthcareProvider.email}</span>
                  <span>{order.healthcareProvider.phone}</span>
                </div>
                {getProfilePic(order.healthcareProvider.firstName)}
              </motion.div>
            </motion.div>
          )}
          <motion.div
            className="test-results-report"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h3>Test results & report</h3>
            <motion.div className="detail-item" whileHover={{ x: 3 }}>
              <span>Delivery Method</span>
              <span>{order.deliveryMethod}</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {order.orderType === "provider" ? (
        <motion.div
          className="requisition-notes-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
        >
          <motion.div className="requisition-form" variants={itemVariants}>
            <h3>Requisition form</h3>
            {order.requisitionForm !== "N/A" ? (
              <motion.a
                href={order.requisitionForm}
                className="download-button"
                download
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                Download requisition form
              </motion.a>
            ) : (
              <motion.button
                className="download-button"
                disabled
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                Requisition form unavailable
              </motion.button>
            )}
          </motion.div>
          <motion.div className="notes" variants={itemVariants}>
            <h3>Notes</h3>
            {order.notes.length > 0 ? (
              order.notes.map((note, index) => (
                <motion.div
                  key={index}
                  className="note-entry"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                >
                  <p>{note.note_text}</p>
                  <span className="note-meta">
                    Added on {formatNoteDate(note.timestamp)}
                  </span>
                </motion.div>
              ))
            ) : (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                N/A
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      ) : (
        <motion.div
          className="sample-collection-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
        >
          <motion.h3 variants={itemVariants}>Notes</motion.h3>
          <motion.div className="collection-details" variants={itemVariants}>
            <div className="collection-box">
              <span className="value">
                {order.notes.length > 0
                  ? order.notes.map((note, index) => (
                      <div key={index}>
                        {note.note_text}
                        <br />
                        <span className="note-meta">
                          Added on {formatNoteDate(note.timestamp)}
                        </span>
                        {index < order.notes.length - 1 && <br />}
                      </div>
                    ))
                  : "N/A"}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OrderDetails;
